/**
 * Test Response 录入页的字段辅助函数：学生名后的星期标记、模板的 Term / Year 归类、Group 与校区的对应。
 */

// ---------------------------------------------------------------------------
// 星期标记：学生名后面的 "(Mon)"，用来区分同名学生
// ---------------------------------------------------------------------------

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * 名字末尾、括号里的星期。半角全角括号、缩写全称、大小写都认 ——
 * 2026-09-14 线上已有 856 条手打的，写法有 "(Tue)" "(TUES)" "(Tuesday)" "（Fri）" 等。
 * 必须带括号：学生 "Ella Sun" 的 Sun 是姓。
 */
const WEEKDAY_SUFFIX = /\s*[(（]\s*(mon(?:day)?|tue(?:s|sday)?|wed(?:nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\.?\s*[)）]\s*$/i;

export const getWeekdayTag = (studentName) => {
  const match = String(studentName || '').match(WEEKDAY_SUFFIX);
  if (!match) return '';
  const prefix = match[1].slice(0, 3).toLowerCase();
  return WEEKDAYS.find(day => day.toLowerCase() === prefix) || '';
};

export const stripWeekdayTag = studentName => String(studentName || '').replace(WEEKDAY_SUFFIX, '');

/** 换成指定星期（统一写成 "Name (Mon)"）；day 为空则去掉星期 */
export const setWeekdayTag = (studentName, day) => {
  const base = stripWeekdayTag(studentName).trimEnd();
  return day ? `${base} (${day})` : base;
};

// ---------------------------------------------------------------------------
// 模板归类：Term 1–4 与 Year 年级
// ---------------------------------------------------------------------------

/** 优先用模板上的 term 字段；没填的从名字里认 "Term 3"（线上有一个 Year 6 的模板漏填了 term） */
export const getTemplateTerm = (template) => {
  const term = Number(template?.term);
  if ([1, 2, 3, 4].includes(term)) return term;
  const match = String(template?.templateName || '').match(/\bterm\s*([1-4])\b/i);
  return match ? Number(match[1]) : null;
};

/** 年级只存在于模板名里（"Year 5 GATE & Scholarships …"），模板本身没有这个字段 */
export const getTemplateYearLevel = (template) => {
  const match = String(template?.templateName || '').match(/\b(?:year|yr)\s*(\d{1,2})\b/i);
  return match ? Number(match[1]) : null;
};

// ---------------------------------------------------------------------------
// Group ↔ 校区
// ---------------------------------------------------------------------------

const squash = text => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * 校区按钮出现之前手打进 Group 的写法（2026-09-14 从 TestData.responseGroup 统计）。
 * 大小写、空格、标点的差异已经由 squash 抹平，这里只需要列缩写和别名。
 */
const CAMPUS_ALIASES = {
  victoriapark: 'vicpark',
  vic: 'vicpark',
  op: 'osbornepark',
  osbpark: 'osbornepark',
};

const campusKey = text => {
  const key = squash(text);
  return CAMPUS_ALIASES[key] || key;
};

export const readResponseGroup = response => String(
  response?.responseGroup || response?.testResponse?.group || response?.group || ''
).trim();

export const findCampusForGroup = (group, campuses) => {
  if (!String(group || '').trim()) return null;
  const key = campusKey(group);
  return (campuses || []).find(campus => squash(campus.name) === key) || null;
};

/**
 * 点了某个校区按钮后实际写进 Group 的字符串。
 *
 * 报告和排名都是在同一个模板内按 Group 字符串原样分组的。如果这个模板里已经有人按
 * "Vic park" 录了 57 条，新录的写成 "Vic Park" 就会在报告里被拆成两个组。所以沿用这个模板里
 * 该校区最常见的写法；模板里还没有这个校区的记录时，才用校区的正式名称。
 */
export const groupSpellingForCampus = (campus, responses) => {
  const counts = new Map();
  (responses || []).forEach((response) => {
    const group = readResponseGroup(response);
    if (group && findCampusForGroup(group, [campus])) {
      counts.set(group, (counts.get(group) || 0) + 1);
    }
  });
  if (counts.size === 0) return campus.name;
  return [...counts.entries()]
    .sort(([groupA, countA], [groupB, countB]) => (
      countB - countA || Number(groupB === campus.name) - Number(groupA === campus.name)
    ))[0][0];
};
